import Vue from 'vue';
import 'intersection-observer';
import { gsap } from 'gsap';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';
gsap.registerPlugin(ScrollTrigger);

Vue.component('hero-section-component', {
    /**
     * Vue.js instance reactive data variables.
     *
     * @return {Object}
     */
    data() {
        return {
            video: null,
            videoPlaying: false,
            videoPaused: false,
            videoStopped: false,
            muted: false,
            timings: {
                current: 0,
                total: 0,
            },
        };
    },

    /**
     * Called when the Vue.js instance is mounted and
     * ready for use.
     *
     * @return {void}
     */
    mounted() {
        this.initBgVideo();

        this.initIntroAnimation();

        ScrollTrigger.create({
            trigger: '.hero-section__video',
            end: 'bottom 50%',
            onLeave: () => {
                if (this.videoPlaying) {
                    this.playVideo();
                }
            },
        });
    },

    /**
     * Vue.js instance methods.
     *
     * @type {Object}
     */
    methods: {
        /**
         * Handle the post event for the newsletter subscribe form
         */
        initBgVideo() {
            const lazyVideos = [].slice.call(
                document.querySelectorAll('.hero-section__background-video')
            );
            let lazyVideoObserver = null;

            if ('IntersectionObserver' in window) {
                lazyVideoObserver = new IntersectionObserver(
                    (entries, observer) => {
                        entries.forEach((video) => {
                            if (video.isIntersecting) {
                                for (const source in video.target.children) {
                                    const videoSource =
                                        video.target.children[source];
                                    if (
                                        typeof videoSource.tagName ===
                                            'string' &&
                                        videoSource.tagName === 'SOURCE'
                                    ) {
                                        videoSource.src =
                                            videoSource.dataset.src;
                                    }
                                }

                                video.target.oncanplay = () => {
                                    video.target.play();
                                    this.video = this.$refs.video;
                                };

                                video.target.currentTime = 0;
                                video.target.load();
                                video.target.classList.add('loaded');
                                lazyVideoObserver.unobserve(video.target);
                            }
                        });
                    }
                );
            }

            lazyVideos.forEach((lazyVideo) => {
                if (lazyVideoObserver) {
                    lazyVideoObserver.observe(lazyVideo);
                }
            });
        },

        initIntroAnimation() {
            const introTl = gsap.timeline({
                paused: true,
                onComplete: () => {
                    this.timings.total = this.video.duration;
                    this.seeker = this.$refs.seeker;
                },
            });

            introTl.to(
                '.hero-section__overlay',
                { duration: 4, autoAlpha: 0, ease: 'power4.out' },
                0
            );

            introTl.to(
                '.hero-section__titles .line-reveal__line',
                { duration: 1, y: '0%', stagger: 0.1, ease: 'power4.out' },
                0.5
            );

            introTl.to(
                '.play-button__holder',
                {
                    duration: 1,
                    y: 0,
                    autoAlpha: 1,
                    ease: 'power4.out',
                },
                1
            );

            introTl.to(
                '.hero__cta-holder',
                {
                    duration: 1,
                    y: 0,
                    autoAlpha: 1,
                    ease: 'power4.out',
                },
                1.1
            );

            introTl.to(
                '.video__progress-bar',
                {
                    duration: 1,
                    y: 0,
                    autoAlpha: 1,
                    ease: 'power4.out',
                },
                1.2
            );

            introTl.play();
        },

        initPlayAnimation() {
            const playVideoTl = gsap.timeline({
                paused: true,
            });

            playVideoTl.to(
                '.hero-section__overlay',
                {
                    duration: 0.5,
                    autoAlpha: 1,
                    ease: 'power4.out',
                    onComplete: () => {
                        gsap.set('.hero-section__video', { autoAlpha: 1 });
                        gsap.set('.hero-section__background', { autoAlpha: 0 });
                    },
                },
                0
            );

            playVideoTl.to(
                '.hero-section__titles .line-reveal__line',
                { duration: 1, y: '100%', stagger: -0.1, ease: 'power4.out' },
                0
            );

            playVideoTl.to(
                '.hero__cta-holder',
                {
                    duration: 1,
                    autoAlpha: 0,
                    ease: 'power4.out',
                },
                0.1
            );

            playVideoTl.to(
                'header.header',
                {
                    duration: 1,
                    autoAlpha: 0,
                    ease: 'power4.out',
                },
                0.2
            );

            playVideoTl.to(
                '.sound-btn',
                {
                    duration: 1,
                    autoAlpha: 1,
                    ease: 'power4.out',
                },
                0.5
            );

            playVideoTl.to(
                '.hero-section__overlay',
                {
                    duration: 1,
                    autoAlpha: 0,
                    ease: 'power4.out',
                    onStart: () => {
                        this.video.muted = false;
                        this.video.play();
                        this.videoPlaying = true;
                        this.initSoundAnimation();
                    },
                },
                0.5
            );

            playVideoTl.play();
        },

        toggleSound() {
            if (this.muted) {
                this.video.muted = false;
                this.muted = false;
                this.soundTl.play(0);

                return;
            }

            this.muted = true;
            this.video.muted = true;
            this.soundTl.kill();
            gsap.to('.sound-btn__line', { duration: 0.2, scaleY: 0.32 });
        },

        initSoundAnimation() {
            // Add Timeline for Line 1 animation circle
            this.line1Tl = gsap.timeline({ repeat: -1 });

            this.line1Tl.to('.sound-btn__line--1', {
                duration: 0.2,
                scaleY: 0.12,
            });
            this.line1Tl.to('.sound-btn__line--1', {
                duration: 0.2,
                scaleY: 0.08,
            });
            this.line1Tl.to('.sound-btn__line--1', {
                duration: 0.2,
                scaleY: 0.25,
            });
            this.line1Tl.to('.sound-btn__line--1', {
                duration: 0.2,
                scaleY: 0.14,
            });
            this.line1Tl.to('.sound-btn__line--1', {
                duration: 0.2,
                scaleY: 0.2,
            });
            this.line1Tl.to('.sound-btn__line--1', {
                duration: 0.2,
                scaleY: 0.08,
            });
            this.line1Tl.to('.sound-btn__line--1', {
                duration: 0.15,
                scaleY: 0.08,
            });

            // Add Timeline for Line 2 animation circle
            this.line2Tl = gsap.timeline({ repeat: -1 });

            this.line2Tl.to('.sound-btn__line--2', {
                duration: 0.2,
                scaleY: 0.2,
            });
            this.line2Tl.to('.sound-btn__line--2', {
                duration: 0.2,
                scaleY: 0.12,
            });
            this.line2Tl.to('.sound-btn__line--2', {
                duration: 0.2,
                scaleY: 0.4,
            });
            this.line2Tl.to('.sound-btn__line--2', {
                duration: 0.2,
                scaleY: 0.08,
            });
            this.line2Tl.to('.sound-btn__line--2', {
                duration: 0.2,
                scaleY: 0.3,
            });
            this.line2Tl.to('.sound-btn__line--2', {
                duration: 0.2,
                scaleY: 0.08,
            });
            this.line2Tl.to('.sound-btn__line--2', {
                duration: 0.15,
                scaleY: 0.08,
            });

            // Add Timeline for Line 3 animation circle
            this.line3Tl = gsap.timeline({ repeat: -1 });

            this.line3Tl.to('.sound-btn__line--3', {
                duration: 0.2,
                scaleY: 0.4,
            });
            this.line3Tl.to('.sound-btn__line--3', {
                duration: 0.2,
                scaleY: 0.2,
            });
            this.line3Tl.to('.sound-btn__line--3', {
                duration: 0.2,
                scaleY: 0.5,
            });
            this.line3Tl.to('.sound-btn__line--3', {
                duration: 0.2,
                scaleY: 0.15,
            });
            this.line3Tl.to('.sound-btn__line--3', {
                duration: 0.2,
                scaleY: 0.4,
            });
            this.line3Tl.to('.sound-btn__line--3', {
                duration: 0.2,
                scaleY: 0.2,
            });
            this.line3Tl.to('.sound-btn__line--3', {
                duration: 0.15,
                scaleY: 0.2,
            });

            // Add Timeline for Line 4 animation circle
            this.line4Tl = gsap.timeline({ repeat: -1 });

            this.line4Tl.to('.sound-btn__line--4', {
                duration: 0.2,
                scaleY: 0.9,
            });
            this.line4Tl.to('.sound-btn__line--4', {
                duration: 0.2,
                scaleY: 0.2,
            });
            this.line4Tl.to('.sound-btn__line--4', {
                duration: 0.2,
                scaleY: 0.8,
            });
            this.line4Tl.to('.sound-btn__line--4', {
                duration: 0.2,
                scaleY: 0.2,
            });
            this.line4Tl.to('.sound-btn__line--4', {
                duration: 0.2,
                scaleY: 0.8,
            });
            this.line4Tl.to('.sound-btn__line--4', {
                duration: 0.2,
                scaleY: 0.6,
            });
            this.line4Tl.to('.sound-btn__line--4', {
                duration: 0.15,
                scaleY: 0.6,
            });

            // Add Timeline for Line 5 animation circle
            this.line5Tl = gsap.timeline({ repeat: -1 });

            this.line5Tl.to('.sound-btn__line--5', {
                duration: 0.2,
                scaleY: 0.3,
            });
            this.line5Tl.to('.sound-btn__line--5', {
                duration: 0.2,
                scaleY: 0.8,
            });
            this.line5Tl.to('.sound-btn__line--5', {
                duration: 0.2,
                scaleY: 0.2,
            });
            this.line5Tl.to('.sound-btn__line--5', {
                duration: 0.2,
                scaleY: 0.6,
            });
            this.line5Tl.to('.sound-btn__line--5', {
                duration: 0.2,
                scaleY: 0.3,
            });
            this.line5Tl.to('.sound-btn__line--5', {
                duration: 0.2,
                scaleY: 0.85,
            });
            this.line5Tl.to('.sound-btn__line--5', {
                duration: 0.15,
                scaleY: 0.85,
            });

            // Add Timeline for Line 6 animation circle
            this.line6Tl = gsap.timeline({ repeat: -1 });

            this.line6Tl.to('.sound-btn__line--6', {
                duration: 0.2,
                scaleY: 0.2,
            });
            this.line6Tl.to('.sound-btn__line--6', {
                duration: 0.2,
                scaleY: 0.6,
            });
            this.line6Tl.to('.sound-btn__line--6', {
                duration: 0.2,
                scaleY: 0.4,
            });
            this.line6Tl.to('.sound-btn__line--6', {
                duration: 0.2,
                scaleY: 0.7,
            });
            this.line6Tl.to('.sound-btn__line--6', {
                duration: 0.2,
                scaleY: 0.3,
            });
            this.line6Tl.to('.sound-btn__line--6', {
                duration: 0.2,
                scaleY: 0.64,
            });
            this.line6Tl.to('.sound-btn__line--6', {
                duration: 0.15,
                scaleY: 0.64,
            });

            // Add Timeline for Line 7 animation circle
            this.line7Tl = gsap.timeline({ repeat: -1 });

            this.line7Tl.to('.sound-btn__line--7', {
                duration: 0.2,
                scaleY: 0.4,
            });
            this.line7Tl.to('.sound-btn__line--7', {
                duration: 0.2,
                scaleY: 0.8,
            });
            this.line7Tl.to('.sound-btn__line--7', {
                duration: 0.2,
                scaleY: 0.3,
            });
            this.line7Tl.to('.sound-btn__line--7', {
                duration: 0.2,
                scaleY: 0.7,
            });
            this.line7Tl.to('.sound-btn__line--7', {
                duration: 0.2,
                scaleY: 0.4,
            });
            this.line7Tl.to('.sound-btn__line--7', {
                duration: 0.2,
                scaleY: 0.72,
            });
            this.line7Tl.to('.sound-btn__line--7', {
                duration: 0.15,
                scaleY: 0.72,
            });

            // Add Timeline for Line 8 animation circle
            this.line8Tl = gsap.timeline({ repeat: -1 });

            this.line8Tl.to('.sound-btn__line--8', {
                duration: 0.2,
                scaleY: 0.4,
            });
            this.line8Tl.to('.sound-btn__line--8', {
                duration: 0.2,
                scaleY: 0.2,
            });
            this.line8Tl.to('.sound-btn__line--8', {
                duration: 0.2,
                scaleY: 0.5,
            });
            this.line8Tl.to('.sound-btn__line--8', {
                duration: 0.2,
                scaleY: 0.15,
            });
            this.line8Tl.to('.sound-btn__line--8', {
                duration: 0.2,
                scaleY: 0.4,
            });
            this.line8Tl.to('.sound-btn__line--8', {
                duration: 0.2,
                scaleY: 0.2,
            });
            this.line8Tl.to('.sound-btn__line--8', {
                duration: 0.15,
                scaleY: 0.2,
            });

            // Add Timeline for Line 9 animation circle
            this.line9Tl = gsap.timeline({ repeat: -1 });

            this.line9Tl.to('.sound-btn__line--9', {
                duration: 0.2,
                scaleY: 0.2,
            });
            this.line9Tl.to('.sound-btn__line--9', {
                duration: 0.2,
                scaleY: 0.12,
            });
            this.line9Tl.to('.sound-btn__line--9', {
                duration: 0.2,
                scaleY: 0.4,
            });
            this.line9Tl.to('.sound-btn__line--9', {
                duration: 0.2,
                scaleY: 0.08,
            });
            this.line9Tl.to('.sound-btn__line--9', {
                duration: 0.2,
                scaleY: 0.3,
            });
            this.line9Tl.to('.sound-btn__line--9', {
                duration: 0.2,
                scaleY: 0.08,
            });
            this.line9Tl.to('.sound-btn__line--9', {
                duration: 0.15,
                scaleY: 0.08,
            });

            // Add Timeline for Line 10 animation circle
            this.line10Tl = gsap.timeline({ repeat: -1 });

            this.line10Tl.to('.sound-btn__line--10', {
                duration: 0.2,
                scaleY: 0.12,
            });
            this.line10Tl.to('.sound-btn__line--10', {
                duration: 0.2,
                scaleY: 0.08,
            });
            this.line10Tl.to('.sound-btn__line--10', {
                duration: 0.2,
                scaleY: 0.3,
            });
            this.line10Tl.to('.sound-btn__line--10', {
                duration: 0.2,
                scaleY: 0.14,
            });
            this.line10Tl.to('.sound-btn__line--10', {
                duration: 0.2,
                scaleY: 0.25,
            });
            this.line10Tl.to('.sound-btn__line--10', {
                duration: 0.2,
                scaleY: 0.08,
            });
            this.line10Tl.to('.sound-btn__line--10', {
                duration: 0.15,
                scaleY: 0.08,
            });

            // Add master timeline for sound animation
            this.soundTl = gsap.timeline({ paused: true });

            this.soundTl.to('.sound-btn__line--1', {
                duration: 0.2,
                scaleY: 0.08,
            });
            this.soundTl.to(
                '.sound-btn__line--2',
                { duration: 0.2, scaleY: 0.08 },
                0
            );
            this.soundTl.to(
                '.sound-btn__line--3',
                { duration: 0.2, scaleY: 0.2 },
                0
            );
            this.soundTl.to(
                '.sound-btn__line--4',
                { duration: 0.2, scaleY: 0.6 },
                0
            );
            this.soundTl.to(
                '.sound-btn__line--5',
                { duration: 0.2, scaleY: 0.85 },
                0
            );
            this.soundTl.to(
                '.sound-btn__line--6',
                { duration: 0.2, scaleY: 0.64 },
                0
            );
            this.soundTl.to(
                '.sound-btn__line--7',
                { duration: 0.2, scaleY: 0.72 },
                0
            );
            this.soundTl.to(
                '.sound-btn__line--8',
                { duration: 0.2, scaleY: 0.2 },
                0
            );
            this.soundTl.to(
                '.sound-btn__line--9',
                { duration: 0.2, scaleY: 0.08 },
                0
            );
            this.soundTl.to(
                '.sound-btn__line--10',
                { duration: 0.2, scaleY: 0.08 },
                0
            );

            this.soundTl.add(this.line1Tl, 0.2);
            this.soundTl.add(this.line5Tl, 0.2);
            this.soundTl.add(this.line8Tl, 0.2);
            this.soundTl.add(this.line2Tl, 0.3);
            this.soundTl.add(this.line7Tl, 0.3);
            this.soundTl.add(this.line3Tl, 0.4);
            this.soundTl.add(this.line9Tl, 0.4);
            this.soundTl.add(this.line6Tl, 0.4);
            this.soundTl.add(this.line10Tl, 0.5);
            this.soundTl.add(this.line4Tl, 0.5);

            this.soundTl.play(0);
        },

        onRangeChange(event) {
            this.rangeUpdate = true;

            // Calculate the new time
            const time =
                this.video.duration * (event.currentTarget.value / 100);

            // Update the video time
            this.video.currentTime = time;

            this.rangeUpdate = false;
        },

        onVideoTimeupdate() {
            if (
                !this.video.paused &&
                !this.rangeUpdate &&
                this.video &&
                this.seeker
            ) {
                // Calculate the slider value
                const value =
                    (100 / this.video.duration) * this.video.currentTime;

                // Update the slider value

                this.seeker.value = value;

                this.progressWidth =
                    (this.$refs.seeker.offsetWidth / 100) * this.seeker.value;
                gsap.killTweensOf(this.$refs.seekbar);
                gsap.set(this.$refs.seekbar, { width: this.progressWidth });
            }
        },

        onVideoEnded() {
            this.videoPlaying = false;
            this.video.currentTime = 0;
        },

        stopVideo() {
            const pauseVideoTl = gsap.timeline({
                paused: true,
            });

            pauseVideoTl.to(
                '.hero-section__overlay',
                {
                    duration: 0.5,
                    autoAlpha: 1,
                    ease: 'power4.out',
                    onStart: () => {
                        this.video.pause();
                        this.videoPaused = false;
                        this.videoPlaying = false;
                        this.videoStopped = true;
                        gsap.set(this.$refs.seekbar, { width: 0 });
                    },
                    onComplete: () => {
                        gsap.set('.hero-section__video', { autoAlpha: 0 });
                        gsap.set('.hero-section__background', {
                            autoAlpha: 1,
                        });
                    },
                },
                0
            );

            pauseVideoTl.to(
                '.sound-btn',
                {
                    duration: 0.5,
                    autoAlpha: 0,
                    ease: 'power4.out',
                },
                0
            );

            pauseVideoTl.to(
                '.hero-section__overlay',
                {
                    duration: 1,
                    autoAlpha: 0,
                    ease: 'power4.out',
                    onStart: () => {},
                },
                0.5
            );

            pauseVideoTl.to(
                '.hero-section__titles .line-reveal__line',
                {
                    duration: 1,
                    y: '0%',
                    stagger: 0.1,
                    ease: 'power4.out',
                },
                0.7
            );

            pauseVideoTl.to(
                'header.header',
                {
                    duration: 1,
                    autoAlpha: 1,
                    ease: 'power4.out',
                },
                0.7
            );

            pauseVideoTl.to(
                '.hero__cta-holder',
                {
                    duration: 1,
                    autoAlpha: 1,
                    ease: 'power4.out',
                },
                1
            );

            pauseVideoTl.play();
        },

        playVideo() {
            this.md = new MobileDetect(window.navigator.userAgent);

            const isIOS =
                /iPad|iPhone|iPod/.test(window.navigator.platform) ||
                (window.navigator.platform === 'MacIntel' &&
                    navigator.maxTouchPoints > 1);

            if (this.md.mobile() || isIOS) {
                this.video.muted = false;
                this.video.play();
                this.video.webkitEnterFullScreen();

                return;
            }

            if (!this.videoPlaying && this.videoPaused) {
                // Paused
                this.video.play();
                this.videoPlaying = true;
            } else if (!this.videoPlaying) {
                // Stopped or initial state
                this.video.oncanplay = () => {
                    this.video.play();
                    this.videoPlaying = true;
                    this.videoPaused = false;
                    this.videoStopped = false;
                    this.initPlayAnimation();
                };

                this.video.currentTime = 0;
                this.video.load();
            } else {
                // Playing
                this.video.pause();
                this.videoPaused = true;
                this.videoPlaying = false;
                this.videoStopped = false;
            }
        },
    },
});
